import React, { useMemo, useState } from "react"
import Select from "react-select"
import TableContainer from "../../../components/Common/TableContainer"
import * as Yup from "yup"
import { useFormik } from "formik"

//import components
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"

import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"
import { Link } from "react-router-dom"
import { formatReadableDate } from "utils/formatDate"
import {
  useCreateUser,
  useDeleteUser,
  useEditUser,
  useUsers,
} from "hooks/userHook"
import { toast } from "react-toastify"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import { useRoles } from "hooks/useRolePermission"
import BadgeList from "components/Common/BadgeList"

const UserList = () => {
  //meta title
  document.title = "User List"

  const { params, setPage, setSearch } = useListParams({
    page: 1,
    search: "",
  })

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [user, setUser] = useState(null)
  const [showPassword, setShowPassword] = useState(false)

  const { data: rolesData } = useRoles({ paginate: false }, modal)

  const roleOptions =
    rolesData?.data?.data?.map(role => ({
      value: role.id,
      label: role.name,
    })) || []

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search ? { search: params.search } : {}),
      ...(params.role ? { role: params.role } : {}),
      ...(params.department ? { department: params.department } : {}),
    }),
    [params]
  )

  // user
  const {
    data: users,
    isLoading,
    refetch: userRefetch,
    isRefetching: userRefetchLoading,
  } = useUsers(queryParams)

  // user mutation
  const { mutateAsync: createUser, isPending: userCreateLoading } =
    useCreateUser()

  const { mutateAsync: editUser, isPending: userEditLoading } = useEditUser()
  const { mutateAsync: deleteUser, isPending: userDeleteLoading } =
    useDeleteUser()

  // validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      // ADDED: Removes whitespace from the beginning and end
      .trim()
      // ADDED: Enforces a minimum length for the name
      .min(2, "Name must be at least 2 characters long")
      .matches(
        /^[a-zA-Z\s.'-]+$/,
        "Name can only contain letters, spaces, and basic punctuation"
      )
      .required("Please enter your name"),

    username: Yup.string()
      .min(3, "Username must be at least 3 characters long")
      .max(20, "Username cannot be longer than 20 characters")
      // ADDED: Restricts username to common allowed characters (no spaces)
      .matches(
        /^[a-zA-Z0-9_]+$/,
        "Username can only contain letters, numbers, and underscores"
      )
      .required("Please enter your username"),

    email: Yup.string().email("Please enter a valid email address"),

    roleIds: Yup.array().of(Yup.number()),
    password: isEdit
      ? Yup.string().matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          "Password must contain an uppercase letter, a lowercase letter, a number, and a special character"
        )
      : Yup.string()
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            "Password must contain an uppercase letter, a lowercase letter, a number, and a special character"
          )
          .required("Please enter your password"),
  })

  // on submit
  const onSubmit = async values => {
    const payload = {
      name: values.name,
      username: values.username,
      email: values.email,
      roleIds: values.roleIds,
      ...(values.password ? { password: values.password } : {}),
    }

    if (isEdit) {
      const userEditedData = await editUser({ id: user.id, ...payload })
      if (userEditedData?.success) {
        toast.success(userEditedData?.message ?? "")
        validation.resetForm()
        toggle()
      }
    } else {
      const userCreatedData = await createUser(payload)
      if (userCreatedData?.success) {
        toast.success(userCreatedData?.message ?? "")
        validation.resetForm()
        toggle()
      }
    }
  }

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      name: (user && user.name) || "",
      username: (user && user.username) || "",
      email: (user && user.email) || "",
      roleIds: (user && user.roles?.map(r => r.id)) || [],
      password: "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const toggle = () => {
    if (modal) {
      setModal(false)
      setUser(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }

  const handleAddNewClick = () => {
    setUser(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleUserListRefresh = () => {
    userRefetch()
  }

  const handleUserClick = arg => {
    const user = arg

    setUser({
      id: user.id,
      name: user.name,
      username: user.username,
      email: user.email,
      password: user.password,
      roles: user?.roles ?? [],
    })

    setIsEdit(true)

    toggle()
  }

  //delete user
  const [deleteModal, setDeleteModal] = useState(false)

  const onClickDelete = user => {
    setUser(user)
    setDeleteModal(true)
  }

  const handleDeleteUser = async () => {
    if (user && user.id) {
      const userDeletedData = await deleteUser(user.id)
      if (userDeletedData?.success) {
        toast.success(userDeletedData?.message ?? "")
        validation.resetForm()
        setDeleteModal(false)
      }
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: cellProps => {
          return (
            <Link to="#" className="text-body fw-bold">
              {cellProps.row.original.id}
            </Link>
          )
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        header: "Username",
        accessorKey: "username",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        header: "Email",
        enableColumnFilter: false,
        enableSorting: true,
        accessorKey: "email",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        header: "Role",
        enableColumnFilter: false,
        enableSorting: true,
        accessorKey: "roles",
        cell: ({ getValue }) => {
          const roles = getValue()
          return (
            <div style={{ maxWidth: 200 }}>
              <BadgeList items={roles} />
            </div>
          )
        },
      },
      {
        header: "Onboarded",
        enableColumnFilter: false,
        enableSorting: true,
        accessorKey: "created_at",
        cell: cellProps => {
          return (
            <>
              {cellProps.row.original.created_at
                ? formatReadableDate(cellProps.row.original.created_at)
                : "-"}
            </>
          )
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: cellProps => {
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li data-bs-toggle="tooltip" data-bs-placement="top" title="View">
                <Link
                  to={`/user-list/${cellProps.row.original.username}/view`}
                  className="btn btn-sm btn-soft-primary"
                  id={`viewtooltip-${cellProps.row.original.id}`}
                >
                  <i className="mdi mdi-eye-outline" />

                  <UncontrolledTooltip
                    placement="top"
                    target={`viewtooltip-${cellProps.row.original.id}`}
                  >
                    View
                  </UncontrolledTooltip>
                </Link>
              </li>

              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-info"
                  onClick={() => {
                    const jobData = cellProps.row.original
                    handleUserClick(jobData)
                  }}
                  id={`edittooltip-${cellProps.row.original.id}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`edittooltip-${cellProps.row.original.id}`}
                  >
                    Edit
                  </UncontrolledTooltip>
                </Link>
              </li>

              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => {
                    const userData = cellProps.row.original
                    onClickDelete(userData)
                  }}
                  id={`deletetooltip-${cellProps.row.original.id}`}
                >
                  <i className="mdi mdi-delete-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`deletetooltip-${cellProps.row.original.id}`}
                  >
                    Delete
                  </UncontrolledTooltip>
                </Link>
              </li>
            </ul>
          )
        },
      },
    ],
    []
  )

  // Here we assume the pagination object is at the root of the response.
  const usersData = users?.data?.data || []
  const paginationInfo = users?.data
    ? {
        current_page: users?.data?.current_page,
        from: users?.data?.from,
        last_page: users?.data?.last_page,
        per_page: users?.data?.per_page,
        to: users?.data?.to,
        total: users?.data?.total,
      }
    : null

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        loading={userDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="User" breadcrumbItem="User Lists" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">User Lists</h5>
                    <div className="flex-shrink-0">
                      <Link
                        to="#!"
                        onClick={handleAddNewClick}
                        className="btn btn-primary me-1"
                      >
                        Add New User
                      </Link>
                      <Link
                        to="#!"
                        onClick={handleUserListRefresh}
                        className="btn btn-light me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            userRefetchLoading ? "mdi-spin" : ""
                          }`}
                        ></i>
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Link>
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>

                  <TableContainer
                    columns={columns}
                    data={usersData} // Pass only the array of users
                    isPagination={true}
                    paginationInfo={paginationInfo} // Pass the pagination object
                    onPageChange={setPage} // Pass the callback function
                    // --- Other styling props ---
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    loading={isLoading || userRefetchLoading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader toggle={toggle} tag="h4">
              {!!isEdit ? "Edit User" : "Add User"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                  return false
                }}
              >
                <Row>
                  <Col className="col-12">
                    <div className="mb-3">
                      <Label> Full Name</Label>
                      <Input
                        name="name"
                        type="text"
                        placeholder="Enter name"
                        validate={{
                          required: { value: true },
                        }}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.name || ""}
                        invalid={
                          validation.touched.name && validation.errors.name
                            ? true
                            : false
                        }
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">
                          {validation.errors.name}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label> Username</Label>
                      <Input
                        name="username"
                        type="text"
                        placeholder="Enter username"
                        validate={{
                          required: { value: true },
                        }}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.username || ""}
                        invalid={
                          validation.touched.username &&
                          validation.errors.username
                            ? true
                            : false
                        }
                      />
                      {validation.touched.username &&
                      validation.errors.username ? (
                        <FormFeedback type="invalid">
                          {validation.errors.username}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label> Email</Label>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Enter email"
                        validate={{
                          required: { value: true },
                        }}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={
                          validation.touched.email && validation.errors.email
                            ? true
                            : false
                        }
                      />
                      {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <Label> Password</Label>

                        <i
                          className={
                            showPassword ? "bx bxs-show" : "bx bxs-hide"
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            // These styles position the icon inside the parent div
                            top: "50%",
                            right: "10px",
                            transform: "translateY(-50%)", // This trick perfectly centers it vertically
                            cursor: "pointer",
                            zIndex: 2, // Ensures the icon is above the input field
                          }}
                          aria-hidden="true"
                        ></i>
                      </div>
                      <Input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        validate={{
                          required: { value: true },
                        }}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.password || ""}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                            ? true
                            : false
                        }
                      />

                      {validation.touched.password &&
                      validation.errors.password ? (
                        <FormFeedback type="invalid">
                          {validation.errors.password}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Label>Role</Label>
                      <Select
                        isMulti
                        name="roleIds"
                        options={roleOptions}
                        value={roleOptions.filter(option =>
                          validation.values.roleIds.includes(option.value)
                        )}
                        onChange={selectedOptions => {
                          validation.setFieldValue(
                            "roleIds",
                            selectedOptions
                              ? selectedOptions.map(opt => opt.value)
                              : []
                          )
                        }}
                        styles={{
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? "#4caf50" // green background when selected
                              : state.isFocused
                              ? "#e0f7fa" // light cyan on hover
                              : "#fff", // default white
                            color: state.isSelected ? "#fff" : "#333",
                          }),
                          multiValue: provided => ({
                            ...provided,
                            backgroundColor: "#4caf50", // selected tag background
                            color: "#fff",
                          }),
                          multiValueLabel: provided => ({
                            ...provided,
                            color: "#fff",
                          }),
                          multiValueRemove: provided => ({
                            ...provided,
                            color: "#fff",
                            ":hover": {
                              backgroundColor: "#388e3c",
                              color: "white",
                            },
                          }),
                        }}
                      />
                      {validation.touched.roleIds &&
                      validation.errors.roleIds ? (
                        <div className="text-danger mt-1">
                          {validation.errors.roleIds}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="text-end">
                      <Button
                        color="success"
                        type="submit"
                        className="save-user"
                      >
                        {!!isEdit
                          ? `Edit${userEditLoading ? "ing" : ""} User`
                          : `Add${userCreateLoading ? "ing" : ""} User`}
                        {userEditLoading || userCreateLoading ? "..." : ""}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default UserList
